import { useParams } from "react-router-dom";
import { LocalizedLink as Link } from "@/components/routing/LocalizedLink";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { PublicShell } from "@/components/layouts/PublicShell";
import { Seo } from "@/components/seo/Seo";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { ArrowLeft, ArrowRight, Map } from "lucide-react";
import { EmptyState } from "@/components/ui/empty-state";
import { supabase } from "@/integrations/supabase/client";
import { useLocalizedField } from "@/hooks/useLocalizedField";

export default function TrackPage() {
  const { slug } = useParams();
  const { t } = useTranslation();
  const pick = useLocalizedField();
  const [track, setTrack] = useState<any>(null);
  const [bootcamps, setBootcamps] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!slug) return;
    (async () => {
      const { data: tr } = await supabase.from("tracks" as any).select("*").eq("slug", slug).maybeSingle();
      if (tr) {
        setTrack(tr);
        const { data: bcs } = await supabase
          .from("bootcamps")
          .select("*")
          .eq("track_id", (tr as any).id)
          .eq("published", true)
          .order("created_at");
        setBootcamps(bcs ?? []);
      }
      setLoading(false);
    })();
  }, [slug]);

  if (loading) {
    return (
      <PublicShell>
        <div className="container py-12 space-y-6">
          <Skeleton className="h-12 w-2/3" />
          <Skeleton className="h-32 w-full" />
        </div>
      </PublicShell>
    );
  }

  if (!track) {
    return (
      <PublicShell>
        <div className="container py-20 text-center">
          <h1 className="font-display text-3xl font-bold">{t("marketing.track.notFound")}</h1>
          <Button asChild className="mt-6"><Link to="/catalog">{t("marketing.detail.backToCatalog")}</Link></Button>
        </div>
      </PublicShell>
    );
  }

  const trackName = pick(track, "name");
  const trackDesc = pick(track, "description");

  return (
    <PublicShell>
      <Seo title={`${trackName} — Bootpreneurs`} description={trackDesc?.slice(0, 160)} />

      {/* Hero */}
      <section className="border-b border-border bg-gradient-to-br from-primary/10 via-background to-background">
        <div className="container py-16 md:py-20">
          <Link to="/catalog" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground">
            <ArrowLeft className="h-4 w-4" /> {t("marketing.detail.backToCatalog")}
          </Link>
          <div className="mx-auto mt-6 max-w-3xl text-center">
            <Badge variant="secondary" className="mb-4">{t("marketing.track.badge")}</Badge>
            <h1 className="font-display text-4xl font-bold md:text-5xl">{trackName}</h1>
            {trackDesc && <p className="mt-4 text-lg text-muted-foreground">{trackDesc}</p>}
            <div className="mt-6 flex justify-center gap-6 text-sm text-muted-foreground">
              <span>{bootcamps.length} {t("marketing.track.bootcampsCount")}</span>
            </div>
          </div>
        </div>
      </section>

      {/* Stage path */}
      <section className="border-b border-border">
        <div className="container py-16">
          <h2 className="mb-10 text-center font-display text-3xl font-bold">{t("marketing.track.pathTitle")}</h2>
          {bootcamps.length === 0 ? (
            <EmptyState
              illustration={<Map className="h-10 w-10" />}
              title={t("marketing.track.empty")}
            />
          ) : (
            <div className="space-y-4">
              {bootcamps.map((bc, idx) => (
                <Link key={bc.id} to={`/catalog/${bc.slug}`} className="group block">
                  <Card className="transition group-hover:-translate-y-0.5 group-hover:border-primary/50 group-hover:shadow-lg">
                    <CardHeader className="flex-row items-start gap-4 space-y-0">
                      <div className="grid h-12 w-12 shrink-0 place-items-center rounded-full bg-primary/10 font-display text-lg font-bold text-primary">
                        {idx + 1}
                      </div>
                      <div className="flex-1">
                        <div className="mb-2 flex flex-wrap items-center gap-2">
                          {bc.level && <Badge variant="outline" className="capitalize">{t(`marketing.catalog.${bc.level}`, { defaultValue: bc.level })}</Badge>}
                          <span className="text-xs text-muted-foreground">{bc.duration_weeks} {t("marketing.catalog.weeks")}</span>
                        </div>
                        <CardTitle className="text-xl group-hover:text-primary">{pick(bc, "title")}</CardTitle>
                        <CardDescription className="mt-1 line-clamp-2">{pick(bc, "subtitle")}</CardDescription>
                      </div>
                      <div className="hidden items-center gap-2 text-sm font-semibold text-primary sm:flex">
                        €{bc.price_eur}
                        <ArrowRight className="h-4 w-4 transition group-hover:translate-x-1" />
                      </div>
                    </CardHeader>
                  </Card>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="bg-muted/30">
        <div className="container py-16 text-center">
          <h2 className="font-display text-3xl font-bold">{t("marketing.track.ctaTitle")}</h2>
          <p className="mt-3 text-muted-foreground">{t("marketing.track.ctaSubtitle")}</p>
          <Button asChild size="lg" className="mt-6"><Link to="/auth">{t("marketing.landing.ctaStart")}</Link></Button>
        </div>
      </section>
    </PublicShell>
  );
}
